import React, { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
} from '@mui/material';

import { useCreateAddendumMutation } from '@/apis/extended/addendumApi';
import { useGetContractQuery } from '@/apis/extended/contractApi';
import GlassButton from '@/components/ui/GlassButton';
import GlassCard from '@/components/ui/GlassCard';

// Removed DatePicker imports - using simple TextField instead

interface CreateAddendumFormProps {
  open: boolean;
  onClose: () => void;
  contractId: string;
  userRole: 'buyer' | 'seller' | 'agent';
}

interface AddendumExtensionDetails {
  requestedExtensionDays: number;
  reason: string;
  newDeadline: string;
  affectedMilestones: string[];
}

const CreateAddendumForm: React.FC<CreateAddendumFormProps> = ({
  open,
  onClose,
  contractId,
  userRole,
}) => {
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [type, setType] = useState<
    | 'amendment'
    | 'extension'
    | 'attachment'
    | 'repair_request'
    | 'name_change'
    | 'general'
    | 'other'
  >('other');
  const [content, setContent] = useState('');
  const [extensionDetails, setExtensionDetails] =
    useState<AddendumExtensionDetails>({
      requestedExtensionDays: 7,
      reason: '',
      newDeadline: '',
      affectedMilestones: [],
    });
  const [createAddendum, { isLoading, error }] = useCreateAddendumMutation();
  const { data: contract } = useGetContractQuery(contractId, {
    skip: !contractId,
  });
  const [localError, setLocalError] = useState('');
  const isRatified = contract?.contractStatus === 'ratified';

  useEffect(() => {
    if (!open) {
      // Reset form when dialog closes
      setTitle('');
      setDescription('');
      setType('other');
      setContent('');
      setLocalError('');
      setExtensionDetails({
        requestedExtensionDays: 7,
        reason: '',
        newDeadline: '',
        affectedMilestones: [],
      });
    }
  }, [open]);

  const handleExtensionDetailChange = (
    field: keyof AddendumExtensionDetails,
    value: any
  ) => {
    setExtensionDetails((prev) => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async () => {
    if (!title || !description) {
      return;
    }

    if (!isRatified) {
      setLocalError('Addendums can only be created after ratification.');
      return;
    }

    if (type === 'extension') {
      if (
        !extensionDetails.reason ||
        extensionDetails.requestedExtensionDays <= 0
      ) {
        return;
      }
    }

    try {
      setLocalError('');
      const addendumData = {
        contractId,
        title,
        description,
        type,
        content,
        extensionDetails: type === 'extension' ? extensionDetails : undefined,
        createdBy: userRole,
      };

      await createAddendum(addendumData).unwrap();

      // Reset form and close dialog
      setTitle('');
      setDescription('');
      setType('other');
      setContent('');
      setExtensionDetails({
        requestedExtensionDays: 7,
        reason: '',
        newDeadline: '',
        affectedMilestones: [],
      });

      onClose();
    } catch (err: any) {
      console.error('Failed to create addendum:', err);
      setLocalError(
        err?.data?.error ||
          err?.data?.message ||
          err?.message ||
          'Failed to create addendum.'
      );
    }
  };

  const getTypeDescription = (type: string) => {
    switch (type) {
      case 'amendment':
        return 'Modify existing contract terms';
      case 'extension':
        return 'Extend deadlines or timeframes';
      case 'attachment':
        return 'Add supporting documents';
      case 'repair_request':
        return 'Request repairs or modifications';
      case 'other':
        return 'Other contract modifications';
      default:
        return '';
    }
  };

  const milestones = [
    'Inspection Period',
    'Financing Contingency',
    'Title Commitment',
    'Closing Date',
  ];

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
      <DialogTitle>Create New Addendum</DialogTitle>
      <DialogContent dividers>
        {(!isRatified || localError || error) && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {!isRatified
              ? 'Addendums can only be created after ratification.'
              : localError ||
                (typeof error === 'string'
                  ? error
                  : (error as any)?.data?.error ||
                    (error as any)?.data?.message ||
                    (error as any)?.message ||
                    'An error occurred')}
          </Alert>
        )}

        <Grid container spacing={2}>
          <Grid item xs={12}>
            <TextField
              autoFocus
              fullWidth
              label="Addendum Title"
              variant="outlined"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="e.g., Extension of Inspection Period"
            />
          </Grid>

          <Grid item xs={12}>
            <TextField
              fullWidth
              multiline
              rows={3}
              label="Description"
              variant="outlined"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Brief description of the addendum..."
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <FormControl fullWidth>
              <InputLabel>Addendum Type</InputLabel>
              <Select
                value={type}
                label="Addendum Type"
                onChange={(e) => setType(e.target.value as any)}
              >
                <MenuItem value="amendment">Amendment</MenuItem>
                <MenuItem value="extension">Extension</MenuItem>
                <MenuItem value="attachment">Attachment</MenuItem>
                <MenuItem value="repair_request">Repair Request</MenuItem>
                <MenuItem value="name_change">Name Change</MenuItem>
                <MenuItem value="general">General</MenuItem>
                <MenuItem value="other">Other</MenuItem>
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12} md={6}>
            <Box sx={{ p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
              <Typography variant="body2" color="text.secondary">
                {getTypeDescription(type)}
              </Typography>
            </Box>
          </Grid>

          {type === 'extension' && (
            <>
              <Grid item xs={12}>
                <Typography variant="h6" gutterBottom>
                  Extension Details
                </Typography>
              </Grid>

              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Requested Extension Days"
                  type="number"
                  variant="outlined"
                  value={extensionDetails.requestedExtensionDays}
                  onChange={(e) =>
                    handleExtensionDetailChange(
                      'requestedExtensionDays',
                      Number(e.target.value)
                    )
                  }
                  inputProps={{ min: 1, max: 90 }}
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="New Deadline"
                  type="date"
                  variant="outlined"
                  value={
                    extensionDetails.newDeadline &&
                    extensionDetails.newDeadline.includes('T')
                      ? extensionDetails.newDeadline.split('T')[0]
                      : extensionDetails.newDeadline || ''
                  }
                  onChange={(e) =>
                    handleExtensionDetailChange(
                      'newDeadline',
                      e.target.value
                        ? new Date(e.target.value).toISOString()
                        : ''
                    )
                  }
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>

              <Grid item xs={12}>
                <TextField
                  fullWidth
                  multiline
                  rows={2}
                  label="Reason for Extension"
                  variant="outlined"
                  value={extensionDetails.reason}
                  onChange={(e) =>
                    handleExtensionDetailChange('reason', e.target.value)
                  }
                  placeholder="Explain why the extension is needed..."
                />
              </Grid>

              <Grid item xs={12}>
                <Typography variant="subtitle2" gutterBottom>
                  Affected Milestones:
                </Typography>
                <Box display="flex" flexWrap="wrap" gap={1}>
                  {milestones.map((milestone) => (
                    <Chip
                      key={milestone}
                      label={milestone}
                      clickable
                      color={
                        extensionDetails.affectedMilestones.includes(milestone)
                          ? 'primary'
                          : 'default'
                      }
                      onClick={() => {
                        const updated =
                          extensionDetails.affectedMilestones.includes(
                            milestone
                          )
                            ? extensionDetails.affectedMilestones.filter(
                                (m) => m !== milestone
                              )
                            : [
                                ...extensionDetails.affectedMilestones,
                                milestone,
                              ];
                        handleExtensionDetailChange(
                          'affectedMilestones',
                          updated
                        );
                      }}
                    />
                  ))}
                </Box>
              </Grid>
            </>
          )}

          {(type === 'amendment' ||
            type === 'other' ||
            type === 'repair_request') && (
            <Grid item xs={12}>
              <TextField
                fullWidth
                multiline
                rows={5}
                label="Addendum Content"
                variant="outlined"
                value={content}
                onChange={(e) => setContent(e.target.value)}
                placeholder="Detailed content of the addendum..."
              />
            </Grid>
          )}

          {type === 'attachment' && (
            <Grid item xs={12}>
              <Alert severity="info">
                For attachments, please upload the file separately after
                creating the addendum. Use this addendum to reference the
                attached documents.
              </Alert>
            </Grid>
          )}
        </Grid>
      </DialogContent>
      <DialogActions>
        <GlassButton onClick={onClose} disabled={isLoading}>
          Cancel
        </GlassButton>
        <GlassButton
          onClick={handleSubmit}
          variant="contained"
          disabled={isLoading || !title || !description || !isRatified}
        >
          {isLoading ? 'Creating...' : 'Create Addendum'}
        </GlassButton>
      </DialogActions>
    </Dialog>
  );
};

export default CreateAddendumForm;
